/**
 * Tough-Bar Builder - Device Module (Step 2)
 * Device selection and image handling methods
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

window.ToughBarBuilder.device = {
  resetLaptopContext() {
    delete this.context.laptop_selected;
    delete this.context.laptop_style;
  },

  resetTabletContext() {
    delete this.context.tablet_make;
    delete this.context.tablet_model;
    delete this.context.tablet_case_type;
    delete this.context.tablet_mount;
    delete this.context.tablet_case;
    delete this.context.tablet_dock;
    delete this.context.tablet_keyboard;
    this.tabletSelectionBackup = null;
    this.tabletSelectionSignature = null;
    this.tabletSelectionConfirmed = null;
  },

  getSelectedMainDeviceType() {
    const confirmed = this.tabletSelectionConfirmed || {};
    const hasConfirmedTablet = !!(confirmed.tablet_make && confirmed.tablet_model && confirmed.tablet_case_type);
    const hasTabletContext = !!(this.context.tablet_make && this.context.tablet_model && this.context.tablet_case_type);
    if (hasConfirmedTablet || hasTabletContext) return 'tablet';

    if (this.context.laptop_selected) return 'laptop';
    if (this.context.other_device_selected) return 'other_device';

    const localKit = (this.localKit && typeof this.localKit === 'object') ? this.localKit : {};
    if (localKit.laptop?.products?.length > 0) return 'laptop';
    if (localKit.other_device?.products?.length > 0) return 'other_device';
    if (localKit.tablet?.products?.length > 0) return 'tablet';

    return null;
  },

  syncDeviceStepState() {
    const step = Number(this.step || 1);
    const selectedType = this.getSelectedMainDeviceType();

    // localKit is a Step 2 staging cache; API response owns kits for other steps.
    if (step !== 2) {
      if (this.localKit && Object.keys(this.localKit).length > 0) {
        this.localKit = {};
      }
      return;
    }

    if (!selectedType) {
      this.localKit = {};
      return;
    }

    const currentLocal = (this.localKit && typeof this.localKit === 'object') ? this.localKit : {};
    const responseKit = (this.response?.kit && typeof this.response.kit === 'object') ? this.response.kit : {};
    const sourceKit = currentLocal[selectedType] || responseKit[selectedType];

    this.localKit = sourceKit && typeof sourceKit === 'object'
      ? { [selectedType]: sourceKit }
      : {};
  },

  isDeviceSelected(deviceId) {
    const selectedType = this.getSelectedMainDeviceType();
    if (selectedType && selectedType !== deviceId) return false;

    const hasKitProducts = this.kit?.[deviceId]?.products?.length > 0;
    if (hasKitProducts) return true;

    if (deviceId === 'tablet') {
      const confirmed = this.tabletSelectionConfirmed || {};
      const hasConfirmedSelection = !!(confirmed.tablet_make && confirmed.tablet_model && confirmed.tablet_case_type);
      if (hasConfirmedSelection) return true;

      // Keep modal edits staged until the user confirms.
      if (typeof this.isTabletSelectionStagedInOpenModal === 'function' && this.isTabletSelectionStagedInOpenModal()) {
        return false;
      }

      return !!(this.context.tablet_make && this.context.tablet_model && this.context.tablet_case_type);
    }
    if (deviceId === 'laptop') return !!this.context.laptop_selected;
    if (deviceId === 'other_device') return !!this.context.other_device_selected;

    return false;
  },

  getImageLayer(imageUrl) {
    if (!imageUrl) return 99;
    const normalizedUrl = String(imageUrl).toLowerCase();
    const fileName = normalizedUrl.split('/').pop() || '';
    const baseFileName = fileName.replace(/\.[a-z0-9]+$/i, '');
    const layerOrder = this.imageLayerOrder || {};
    const keyboardLayer = layerOrder.Keyboard ?? 0.5;
    const mountLayer = layerOrder.Mount ?? 4;
    const keyboardSupportLayer = mountLayer + 0.5;
    const isKeyboardSupportAsset = /(mount|map|support|swing|arm|pole|post|bracket|tray)/.test(fileName);
    const isKeyboardImageAsset = /^ram-kb/.test(baseFileName) || /^ramkb/.test(baseFileName);

    // Primary path-based mapping (deterministic).
    if (normalizedUrl.includes('/tablet-holder/')) {
      return layerOrder.Dock ?? 1;
    }
    if (normalizedUrl.includes('/tablet-case/')) {
      return layerOrder.Case ?? 2;
    }

    // Split keyboard assets into distinct layers:
    // - actual keyboard image (RAM-KB*) in front of tablet case and tablet mount
    // - any other keyboard-path asset remains in the support/mount layer
    if (normalizedUrl.includes('/keyboard/')) {
      if (isKeyboardImageAsset && !isKeyboardSupportAsset) {
        return keyboardLayer;
      }
      return keyboardSupportLayer;
    }

    if (normalizedUrl.includes('/tablet-mount/')) {
      return mountLayer;
    }
    if (normalizedUrl.includes('/laptop/')) {
      return layerOrder.Laptop ?? 5;
    }
    if (normalizedUrl.includes('/tough-bar/tough-bar/')) {
      return layerOrder['Tough-Bar'] ?? 6;
    }

    // Keep keyboard-map overlays beneath keyboard imagery if naming varies.
    if (
      /keyboard[\s/_-]*(map|mount|support|swing|arm|pole|post|bracket|tray)/.test(normalizedUrl)
      || /(map|mount|support|swing|arm|pole|post|bracket|tray)[\s/_-]*keyboard/.test(normalizedUrl)
    ) {
      return keyboardSupportLayer;
    }

    // Fallback token matching against filename only (avoid shared path collisions).
    for (const [layerName, order] of Object.entries(this.imageLayerOrder)) {
      const normalizedLayerName = String(layerName).toLowerCase().replace(/[^a-z0-9]+/g, '');
      if (!normalizedLayerName) continue;
      const comparableName = baseFileName.replace(/[^a-z0-9]+/g, '');
      if (comparableName.includes(normalizedLayerName)) {
        return order;
      }
    }
    return 99;
  },

  async selectDevice(deviceId, options = {}) {
    const { syncSnapshot = true } = options;
    // For simple devices (laptop, other_device), set context and store kit locally
    // Do NOT call API - that happens when user clicks Next
    const button = this.deviceButtons.find(b => b.id === deviceId);
    const changedContextKeys = ['main_device', 'tablet_selection'];

    if (deviceId === 'laptop') {
      // Clear any tablet/other context
      this.resetTabletContext();
      delete this.context.other_device_selected;
      this.context.laptop_selected = true;
      if (!this.context.laptop_style && button?.kit?.style) {
        this.context.laptop_style = button.kit.style;
      }

      // Store laptop kit locally, clear other device kits
      this.localKit = {
        laptop: button?.kit || { products: [], images: [] },
        tablet: { products: [], images: [] },
        other_device: { products: [], images: [] }
      };
    } else if (deviceId === 'other_device') {
      // Clear any tablet/laptop context
      this.resetTabletContext();
      this.resetLaptopContext();
      this.context.other_device_selected = true;

      // Store other_device kit locally, clear other device kits
      this.localKit = {
        other_device: button?.kit || { products: [], images: [] },
        laptop: { products: [], images: [] },
        tablet: { products: [], images: [] }
      };
    }

    if ((deviceId === 'laptop' || deviceId === 'other_device') && typeof this.pruneAccessoriesForContextChange === 'function') {
      this.pruneAccessoriesForContextChange(changedContextKeys);
    }

    this.syncDeviceStepState();

    if (
      syncSnapshot
      && (deviceId === 'laptop' || deviceId === 'other_device')
      && this.step >= 2
      && typeof this.syncSnapshotWithCurrentState === 'function'
    ) {
      this.syncSnapshotWithCurrentState(true).catch(snapshotError => {
        console.warn('[Tough-Bar Device] Snapshot refresh failed:', snapshotError);
      });
    }

    // Fetch product data for the kit
    const kit = this.localKit[deviceId];
    if (kit?.products?.length > 0) {
      const fetchPromises = kit.products.map(handle => this.fetchProductData(handle));
      await Promise.all(fetchPromises);
    }
  },

  async selectDeviceAndProceed(deviceId) {
    await this.selectDevice(deviceId, { syncSnapshot: false });

    if (this.step === 2 && (deviceId === 'laptop' || deviceId === 'other_device') && this.canProceed) {
      await this.nextStep();
      if (!this.error && this.step >= 3 && typeof this.clearStepSnapshotId === 'function') {
        this.clearStepSnapshotId(2);
      }
    }
  },

  handleImageError(handle, event) {
    if (!this.failedImages.includes(handle)) {
      this.failedImages.push(handle);
    }
    event.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect fill="#f3f4f6" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#9ca3af" font-family="system-ui" font-size="14">Image unavailable</text></svg>');
  },

  getProductImage(handle) {
    const product = this.products[handle];
    if (!product) return null;
    if (this.failedImages.includes(handle)) return null;
    return product.featured_image || product.images?.[0] || null;
  },
};
